import { useEffect } from "react"
import { useToast } from "@/hooks/use-toast"
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "@/components/ui/toast"

// Lift the toast above any bottom-right action buttons (Next, Save,
// etc.) once the user scrolls close enough to the bottom of the page
// that those buttons would be in view behind the toast. Threshold is
// the proximity to document bottom, lift is the bottom offset applied
// when within the threshold.
const TOAST_LIFT_THRESHOLD_PX = 160;
const TOAST_LIFT_BOTTOM = "6rem";
const TOAST_DEFAULT_BOTTOM = "1rem";

export function Toaster() {
  const { toasts } = useToast()

  useEffect(() => {
    const update = () => {
      const fromBottom =
        document.documentElement.scrollHeight - (window.scrollY + window.innerHeight);
      const lifted = fromBottom < TOAST_LIFT_THRESHOLD_PX;
      document.documentElement.style.setProperty(
        "--toast-bottom",
        lifted ? TOAST_LIFT_BOTTOM : TOAST_DEFAULT_BOTTOM,
      );
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      document.documentElement.style.removeProperty("--toast-bottom");
    };
  }, []);

  return (
    <ToastProvider>
      {/* Show only the most recent toast — the rest stay queued in state and
          surface one-by-one as each is dismissed (so they "stack" behind it
          rather than all appearing at once). */}
      {toasts.slice(0, 1).map(function ({ id, title, description, action, ...props }) {
        return (
          <Toast key={id} {...props}>
            <div className="grid gap-1">
              {title && <ToastTitle>{title}</ToastTitle>}
              {description && (
                <ToastDescription>{description}</ToastDescription>
              )}
            </div>
            {action}
            <ToastClose />
          </Toast>
        )
      })}
      <ToastViewport />
    </ToastProvider>
  )
}
